import { CheckCircle2, XCircle, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

export const SaintCandidacy = ({
  onQuiz,
}: {
  onBook?: () => void;
  onQuiz?: () => void;
}) => (
  <section className="py-16 sm:py-20 bg-background" id="is-saint-right">
    <div className="container-px mx-auto max-w-5xl px-4">
      <div className="text-center mb-10">
        <h2 className="font-headings text-2xl font-bold text-primary sm:text-3xl lg:text-4xl">
          Could SAINT be right for you?
        </h2>
        <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">
          Take a quick, private quiz to see if this accelerated 5-day protocol
          may fit your situation. Your answers stay on this page until you
          choose to send them.
        </p>
      </div>

      {/* Who SAINT May Help Infographic */}
      <div className="mb-10 mx-auto max-w-4xl overflow-hidden rounded-3xl border border-border bg-card p-3 sm:p-5 shadow-premium">
        <img
          src="https://vibe.filesafe.space/1787327131284421342/attachments/fee94383-0de6-4265-b9ed-102a23e6ae2d.png"
          alt="Who SAINT® TMS May Help: Adults with major depression, people who have tried antidepressants without enough relief, those seeking a non-invasive option, people looking for an accelerated treatment schedule, and patients ready to explore advanced care with a provider"
          className="w-full h-auto rounded-2xl object-contain"
          loading="lazy"
          width={1200}
          height={900}
        />
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="rounded-2xl border border-accent/30 bg-accent/5 p-6 shadow-soft">
          <div className="flex items-center gap-3 mb-4">
            <CheckCircle2 className="h-6 w-6 text-accent" />
            <h3 className="font-headings text-lg font-bold text-primary">
              Good candidates
            </h3>
          </div>
          <ul className="space-y-2.5">
            {[
              "Adults 22 and older",
              "Diagnosed major depressive disorder",
              "Tried at least two antidepressants without adequate improvement",
              "Able to attend five consecutive full days",
              "Able to have an fMRI",
            ].map((item) => (
              <li
                key={item}
                className="flex items-start gap-2 text-sm text-foreground/85 leading-relaxed"
              >
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
          <div className="flex items-center gap-3 mb-4">
            <XCircle className="h-6 w-6 text-destructive/70" />
            <h3 className="font-headings text-lg font-bold text-primary">
              Not eligible
            </h3>
          </div>
          <ul className="space-y-2.5">
            {[
              "Non-removable metal or magnetic-sensitive materials within 30 cm (12 inches) of the treatment coil — including aneurysm clips, bullet fragments, ocular implants, and stents",
              "Active or inactive implanted devices: pacemakers, deep brain stimulators, cochlear implants, or vagus nerve stimulators",
              "Increased intracranial pressure",
              "Intracardiac lines or implanted medication pumps",
            ].map((item) => (
              <li
                key={item}
                className="flex items-start gap-2 text-sm text-foreground/85 leading-relaxed"
              >
                <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive/60" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs text-muted-foreground leading-relaxed">
            These contraindications reflect the FDA-cleared SAINT®
            Neuromodulation System label. Your provider will screen for all of
            these during your consultation.
          </p>
        </div>
      </div>

      <div className="mt-8 flex flex-col items-center gap-4 text-center">
        <Button
          onClick={onQuiz}
          size="lg"
          className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
        >
          <HelpCircle className="h-5 w-5 mr-2" />
          Take the 2-Minute SAINT Quiz
        </Button>
        <p className="text-xs text-muted-foreground max-w-md">
          This quiz is for information only and isn't a diagnosis. A clinician
          confirms eligibility during your consultation.
        </p>
      </div>
    </div>
  </section>
);
